import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import style from '../../../../../scss/components/form.module.scss';
import '../../../../../scss/external/select.scss';
import useUpdateEffect from '../../../../Hooks/useUpdateEffect';
import Field from '../Field';

const CategoryDropdown = ({
    entity,
    onChange,
    service,
    name,
    type,
    isMultiple,
    ignoreOption,
    placeholder,
    parentField,
    parentQueryKey,
    requestName,
    excludedFields,
    requiredField,
    exclusiveOptionLabel,
    ...props
}) => {
    const { t } = useTranslation();

    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const toOption = (item) => {
        if (!item) return null;

        return {
            value: item.id ?? item.uuid ?? item.value,
            label: item.name ?? item.title ?? item.label,
            ...item,
        };
    };

    const extractId = (item) => {
        if (!item) return null;

        if (Array.isArray(item)) {
            const first = item[0];

            return extractId(first);
        }

        if (typeof item === 'number' || typeof item === 'string') {
            return item;
        }

        return item.id ?? item.uuid ?? item.value ?? null;
    };

    const normalizeSelected = (rawValue) => {
        if (!rawValue) {
            return isMultiple ? [] : null;
        }

        if (Array.isArray(rawValue)) {
            return rawValue.map(toOption).filter(Boolean);
        }

        return toOption(rawValue);
    };

    const getOptionValue = (item) =>
        item?.value ?? item?.id ?? item?.uuid ?? null;

    const areOptionsEqual = (a, b) => {
        if (Array.isArray(a) || Array.isArray(b)) {
            const arrA = Array.isArray(a) ? a : [];
            const arrB = Array.isArray(b) ? b : [];

            if (arrA.length !== arrB.length) {
                return false;
            }

            const valuesA = arrA
                .map(getOptionValue)
                .filter(Boolean)
                .map(String)
                .sort();

            const valuesB = arrB
                .map(getOptionValue)
                .filter(Boolean)
                .map(String)
                .sort();

            return valuesA.every((value, index) => value === valuesB[index]);
        }

        return String(getOptionValue(a)) === String(getOptionValue(b));
    };

    const fieldValue = entity[name];

    const primaryCategoryId = extractId(entity.primary_category);
    const subCategoryId = extractId(entity.sub_category);
    const subSubCategoryId = extractId(entity.sub_sub_category);

    const initialSelected = useMemo(() => {
        return normalizeSelected(fieldValue);
    }, [fieldValue, isMultiple]);

    const [selected, setSelected] = useState(initialSelected);

    const parentId = parentField ? extractId(entity[parentField]) : null;

    const requiredFieldId = requiredField
        ? extractId(entity[requiredField])
        : null;

    const isUnavailable = Boolean(requiredField && !requiredFieldId);

    const excludedIdsKey = useMemo(() => {
        return excludedFields
            .map((field) => {
                if (field === 'primary_category') return primaryCategoryId;
                if (field === 'sub_category') return subCategoryId;
                if (field === 'sub_sub_category') return subSubCategoryId;

                return extractId(entity[field]);
            })
            .filter(Boolean)
            .map(String)
            .sort()
            .join(',');
    }, [excludedFields, primaryCategoryId, subCategoryId, subSubCategoryId]);

    const excludedIds = useMemo(() => {
        return excludedIdsKey ? excludedIdsKey.split(',') : [];
    }, [excludedIdsKey]);

    const normalizeExclusiveSelection = (value) => {
        if (!exclusiveOptionLabel || !isMultiple || !Array.isArray(value)) {
            return value;
        }

        const exclusiveLabel = exclusiveOptionLabel.trim().toLowerCase();

        const exclusiveOption = value.find(
            (option) =>
                String(option.label ?? '')
                    .trim()
                    .toLowerCase() === exclusiveLabel,
        );

        if (!exclusiveOption) {
            return value;
        }

        const previousHadExclusive = Array.isArray(selected)
            ? selected.some(
                  (option) =>
                      String(option.label ?? '')
                          .trim()
                          .toLowerCase() === exclusiveLabel,
              )
            : false;

        return previousHadExclusive
            ? value.filter(
                  (option) =>
                      String(option.label ?? '')
                          .trim()
                          .toLowerCase() !== exclusiveLabel,
              )
            : [exclusiveOption];
    };

    useEffect(() => {
        if (!selected || isMultiple) {
            return;
        }

        const selectedId = extractId(selected);

        if (excludedIds.includes(String(selectedId))) {
            setSelected(null);
        }
    }, [selected, excludedIds, isMultiple]);

    useUpdateEffect(() => {
        if (!parentField) {
            return;
        }

        setSelected(isMultiple ? [] : null);
    }, [parentId]);

    useEffect(() => {
        setSelected((current) => {
            if (areOptionsEqual(current, initialSelected)) {
                return current;
            }

            return initialSelected;
        });
    }, [initialSelected]);

    useEffect(() => {
        let cancelled = false;

        if ((parentField && !parentId) || isUnavailable) {
            setOptions([]);
            setIsLoading(false);
            setSelected((current) => {
                const emptyValue = isMultiple ? [] : null;

                if (areOptionsEqual(current, emptyValue)) {
                    return current;
                }

                return emptyValue;
            });

            return undefined;
        }

        setIsLoading(true);

        const params = new URLSearchParams({
            type,
            name: requestName || name,
        });

        if (parentField && parentQueryKey && parentId) {
            params.append(parentQueryKey, String(parentId));
        }

        if (requestName === 'secondaryCategories') {
            if (primaryCategoryId) {
                params.append('primaryCategoryId', String(primaryCategoryId));
            }

            if (subCategoryId) {
                params.append('subCategoryId', String(subCategoryId));
            }

            if (subSubCategoryId) {
                params.append('subSubCategoryId', String(subSubCategoryId));
            }
        }

        service
            .get(`categories?${params.toString()}`)
            .then((result) => {
                if (cancelled) return;

                const data = Array.isArray(result)
                    ? result
                    : result?.data ?? [];

                const mapped = data.map(toOption).filter(Boolean);

                const needle = ignoreOption
                    ? String(ignoreOption).trim().toLowerCase()
                    : null;

                const filtered = mapped.filter((option) => {
                    const isIgnoredByName = needle
                        ? String(option.label ?? '')
                              .trim()
                              .toLowerCase() === needle
                        : false;

                    const isExcludedById = excludedIds.includes(
                        String(option.value),
                    );

                    return !isIgnoredByName && !isExcludedById;
                });

                setOptions(filtered);

                if (!filtered.length) {
                    setSelected((current) => {
                        const emptyValue = isMultiple ? [] : null;

                        if (areOptionsEqual(current, emptyValue)) {
                            return current;
                        }

                        return emptyValue;
                    });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setOptions([]);
                    setSelected((current) => {
                        const emptyValue = isMultiple ? [] : null;

                        if (areOptionsEqual(current, emptyValue)) {
                            return current;
                        }

                        return emptyValue;
                    });
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [
        service,
        type,
        name,
        requestName,
        ignoreOption,
        parentField,
        parentQueryKey,
        parentId,
        isMultiple,
        isUnavailable,
        excludedIdsKey,
        primaryCategoryId,
        subCategoryId,
        subSubCategoryId,
    ]);

    useEffect(() => {
        if (!options.length) {
            return;
        }

        const matchOne = (item) => {
            if (!item) return null;

            const key = item.value ?? item.id ?? item.uuid;

            return options.find((o) => o.value === key) || item;
        };

        if (isMultiple) {
            setSelected((current) => {
                const nextValue = Array.isArray(current)
                    ? current.map(matchOne).filter(Boolean)
                    : [];

                if (areOptionsEqual(current, nextValue)) {
                    return current;
                }

                return nextValue;
            });
        } else {
            setSelected((current) => {
                const nextValue = current ? matchOne(current) : null;

                if (areOptionsEqual(current, nextValue)) {
                    return current;
                }

                return nextValue;
            });
        }
    }, [options, isMultiple]);

    useUpdateEffect(() => {
        const nextValue = isMultiple ? selected || [] : selected || null;

        if (areOptionsEqual(initialSelected, nextValue)) {
            return;
        }

        onChange({
            [name]: nextValue,
        });
    }, [selected, initialSelected, name, isMultiple]);

    return (
        <div className={classNames(style.formGroup, props.className)}>
            <Field {...props} name={name}>
                {(hasError, attributes, isChanged) => (
                    <Select
                        inputId={attributes.id}
                        name={name}
                        isMulti={isMultiple}
                        isClearable
                        isLoading={isLoading}
                        isDisabled={isUnavailable}
                        options={options}
                        value={selected}
                        onChange={(value) =>
                            setSelected(normalizeExclusiveSelection(value))
                        }
                        placeholder={placeholder || t('application.categories')}
                        noOptionsMessage={() => t('application.noResults')}
                        className={classNames(
                            style.autocomplete,
                            `-${name}`,
                            { [style.changedInput]: isChanged },
                            { [style.isInvalid]: hasError },
                        )}
                        classNamePrefix="rs"
                        menuPlacement="auto"
                    />
                )}
            </Field>
        </div>
    );
};

export default CategoryDropdown;

CategoryDropdown.propTypes = {
    entity: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    service: PropTypes.object.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    excludedFields: PropTypes.arrayOf(PropTypes.string),
    requiredField: PropTypes.string,
    isMultiple: PropTypes.bool,
    ignoreOption: PropTypes.string,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    parentField: PropTypes.string,
    parentQueryKey: PropTypes.string,
    requestName: PropTypes.string,
    exclusiveOptionLabel: PropTypes.string,
};

CategoryDropdown.defaultProps = {
    excludedFields: [],
    isMultiple: true,
    ignoreOption: '',
    placeholder: '',
    className: '',
    parentField: '',
    requiredField: '',
    parentQueryKey: '',
    requestName: '',
    exclusiveOptionLabel: '',
};
